import { Button, Flex, Select, Stack, Stepper } from "@mantine/core";
import { useForm, UseFormReturnType } from "@mantine/form";
import { useEffect, useState } from "react";
import { Else, If, Then, When } from "react-if";
import Field from "./FieldBuilder.component";
import { useCreateServicesForm } from "../../../../api/services/useServices";
import { actions } from "../../../../store/useStore";
import { useApplicationStagesAndReasons } from "../../../../api/applications/useApplications";
import { has, isNil, omitBy } from "lodash";

export default function DynamicFormBuilder({
  singleForm,
  isUpdateForm = false,
  updateProps,
  modalProps,
  onSubmit,
  isPublicForm = false,
}: {
  singleForm: any;
  isUpdateForm?: boolean;
  updateProps?: {
    initialValues?: any;
    form?: UseFormReturnType<any, (values: any) => any>;
    onSubmit?: (e: any) => void;
  };
  modalProps?: {
    opened: boolean;
    onClose: () => void;
  };
  onSubmit?: {
    successPage?: string;
    errorPage?: string;
    callback?: (values: any) => void;
  };
  isPublicForm?: boolean;
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [totalSteps] = useState(singleForm?.step_count);

  const initialValues: any = {};
  const validationRules: any = {};

  Object.keys(singleForm?.fields || {}).forEach((stepKey) => {
    singleForm?.fields[stepKey]?.forEach((field: any) => {
      const fieldKey = `${field.name}`;
      const isArrayField =
        field.type === "checkbox" || field.type === "multi_select";
      const isDateField = field.type === "date";

      initialValues[fieldKey] = isUpdateForm
        ? updateProps?.initialValues?.[fieldKey] ??
          (isArrayField ? [] : isDateField ? null : "")
        : field.default_value || (isArrayField ? [] : isDateField ? null : "");

      if (field.required) {
        validationRules[fieldKey] = (value: any) =>
          !value ? "This field is required" : null;
      }
    });
  });

  // updateProps?.form?.setValues({
  //   data: updateProps?.initialValues,
  //   form_identifier: singleForm?.identifier,
  // });

  const _form = useForm({
    initialValues,
    validate: validationRules,
    onValuesChange: (values) => {
      if (isUpdateForm && updateProps?.form) {
        updateProps?.form.setValues({
          data: values,
          form_identifier: singleForm?.identifier,
        });
      }
    },
  });

  const { data: subStagesAndReasons } = useApplicationStagesAndReasons({
    stage_id: updateProps?.form?.values?.stage_id,
    enabled:
      has(updateProps?.form?.values, "stage_id") &&
      !isNil(updateProps?.form?.values?.stage_id),
  });

  const { mutateAsync: createEntry, isPending: isCreateEntryPending } =
    useCreateServicesForm({
      isPublicEndpoint: isPublicForm ?? false,
    });

  const handleSubmit = async (values: any) => {
    try {
      const formattedValues = { ...values };
      Object.keys(singleForm?.fields || {}).forEach((stepKey) => {
        singleForm?.fields[stepKey]?.forEach((field: any) => {
          if (field.type === "date" && formattedValues[field.name]) {
            const utcDate = new Date(formattedValues[field.name]);
            const localDate = new Date(
              utcDate.getTime() - utcDate.getTimezoneOffset() * 60000
            );
            formattedValues[field.name] = localDate.toISOString().split("T")[0];
          }
        });
      });

      const res = await createEntry({
        data: {
          data: omitBy(formattedValues, (value) => value === ""),
          form_identifier: singleForm?.identifier,
        },
      });

      if (modalProps?.opened) {
        modalProps.onClose();
      }

      if (onSubmit?.callback) {
        onSubmit.callback(res);
      }

      if (onSubmit?.successPage) {
        actions.core.route(onSubmit.successPage);
      }
    } catch (error) {
      console.error("Error creating entry:", error);
      actions.core.throwError("An error occurred while creating the entry.");
    }
  };

  const validateStep = (stepIndex: number) => {
    const stepFields = singleForm?.fields?.[stepIndex + 1] || [];
    const stepErrors: any = {};

    stepFields.forEach((field: any) => {
      const fieldKey = `${field.name}`;
      const value = _form.values[fieldKey];

      if (field.required && !value) {
        stepErrors[fieldKey] = "This field is required";
      }
    });

    return Object.keys(stepErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep((current) => Math.min(current + 1, totalSteps - 1));
    }
  };

  useEffect(() => {
    if (isUpdateForm && updateProps?.form && singleForm) {
      // _form.setInitialValues(initialValues);
      updateProps.form.setValues({
        data: initialValues,
        form_identifier: singleForm?.identifier,
      });
    }
  }, [isUpdateForm, singleForm]);

  return (
    <>
      <If condition={singleForm?.has_step && !isUpdateForm}>
        <Then>
          <form
            onSubmit={
              isUpdateForm
                ? _form.onSubmit(
                    (values) =>
                      updateProps?.onSubmit &&
                      updateProps?.onSubmit({ ...values })
                  )
                : _form.onSubmit(handleSubmit)
            }
          >
            <Stepper active={activeStep} onStepClick={() => {}}>
              {Array.from({ length: singleForm?.step_count }).map(
                (_, stepIndex) => (
                  <Stepper.Step key={stepIndex} label={`Step ${stepIndex + 1}`}>
                    <Stack>
                      <When
                        condition={
                          stepIndex === 0 &&
                          subStagesAndReasons?.sub_stages?.length > 0
                        }
                      >
                        <Select
                          mb={16}
                          withAsterisk
                          label="Select sub status"
                          placeholder="Please select sub status"
                          data={subStagesAndReasons?.sub_stages?.map(
                            (value: any) => ({
                              label: value?.label,
                              value: String(value?.value),
                            })
                          )}
                        />
                      </When>

                      {singleForm?.fields?.[stepIndex + 1]?.map(
                        (field: any) => (
                          <Field
                            form={_form}
                            key={field.name}
                            field={field}
                            fields={singleForm?.fields?.[stepIndex + 1]}
                            value={_form.values[`${field.name}`] || ""}
                            onChange={(value: any) => {
                              _form.setFieldValue(field.name, value);
                            }}
                          />
                        )
                      )}

                      <When
                        condition={
                          stepIndex === 0 &&
                          subStagesAndReasons?.reasons?.length > 0
                        }
                      >
                        <Select
                          mb={16}
                          withAsterisk
                          label="Select Reason"
                          placeholder="Please select reason"
                          data={subStagesAndReasons?.reasons?.map(
                            (value: any) => ({
                              label: value?.label,
                              value: String(value?.value),
                            })
                          )}
                        />
                      </When>
                    </Stack>
                  </Stepper.Step>
                )
              )}
            </Stepper>

            <Flex gap={12} mt={24}>
              <Button
                disabled={activeStep === 0}
                onClick={() =>
                  setActiveStep((current: any) => Math.max(current - 1, 0))
                }
              >
                Previous
              </Button>

              <If condition={activeStep === totalSteps - 1}>
                <Then>
                  <Button type="submit" loading={isCreateEntryPending}>
                    Submit
                  </Button>
                </Then>
                <Else>
                  <Button onClick={handleNextStep}>Next</Button>
                </Else>
              </If>
            </Flex>
          </form>
        </Then>
        <Else>
          <form
            onSubmit={(e) => {
              if (isUpdateForm) {
                e.preventDefault();
                updateProps?.onSubmit && updateProps?.onSubmit(e);
              } else {
                _form.onSubmit(handleSubmit)(e);
              }
            }}
          >
            <Stack>
              <When condition={subStagesAndReasons?.sub_stages?.length > 0}>
                <Select
                  mb={16}
                  withAsterisk
                  label="Select sub status"
                  placeholder="Please select sub status"
                  data={subStagesAndReasons?.sub_stages?.map((value: any) => ({
                    label: value?.label,
                    value: String(value?.value),
                  }))}
                />
              </When>

              {singleForm?.fields?.[1]?.map((field: any) => (
                <Field
                  form={_form}
                  field={field}
                  key={field.name}
                  fields={singleForm?.fields?.[1]}
                  value={_form.values[`${field.name}`] || null}
                  onChange={(value: any) => {
                    _form.setFieldValue(field.name, value);
                  }}
                />
              ))}

              <When condition={subStagesAndReasons?.reasons?.length > 0}>
                <Select
                  mb={16}
                  withAsterisk
                  label="Select Reason"
                  placeholder="Please select reason"
                  data={subStagesAndReasons?.reasons?.map((value: any) => ({
                    label: value?.label,
                    value: String(value?.value),
                  }))}
                />
              </When>

              <Button type="submit" loading={isCreateEntryPending}>
                Submit
              </Button>
            </Stack>
          </form>
        </Else>
      </If>
    </>
  );
}
